/**
 * Centralized WMS inventory + cart guard layer.
 * Source of truth: /public/products/weights/products-batch response.
 * Exposed as window.AOneWmsAvailability and window.WMSInventory.
 */
(function () {
  'use strict';

  const STATUS_IDLE = 'idle';
  const STATUS_LOADING = 'loading';
  const STATUS_LOADED = 'loaded';
  const STATUS_ERROR = 'error';

  const WMS_STATUS_AVAILABLE = 'AVAILABLE';
  const WMS_STATUS_COMING_SOON = 'COMING_SOON';
  /** Raw WMS qty > 0 but every serial is already in this customer's cart. */
  const WMS_STATUS_ALL_IN_CART = 'ALL_IN_CART';

  const LABEL_ADD_TO_CART = 'ADD TO CART';
  const LABEL_CHECKING = 'Checking availability...';
  const LABEL_COMING_SOON = 'COMING SOON';
  const LABEL_AVAILABLE = 'AVAILABLE';
  const LABEL_ALL_IN_CART = 'All Available Qty in Cart';
  /** Retained for API compat — never shown as a final customer-facing ATC label. */
  const LABEL_UNAVAILABLE = 'COMING SOON';

  const CART_INVENTORY_EVENT = 'aone:cart-inventory-updated';
  const INVENTORY_STATE_EVENT = 'aone:inventory-state-updated';

  /** @type {Map<string, any>} */
  const productState = new Map();
  /** @type {Map<string, { productId: string, sku: string }>} */
  const variantLookup = new Map();
  /** @type {Map<string, string>} */
  const skuToProductId = new Map();
  /** @type {Map<string, Set<(entry: any) => void>>} */
  const subscribers = new Map();
  /** @type {Set<(detail: any) => void>} */
  const globalSubscribers = new Set();
  /** @type {Set<(detail: any) => void>} */
  const cartChangeSubscribers = new Set();

  /** @type {Set<string>} */
  const reservedSerials = new Set();
  /** @type {Set<string>} */
  const pendingSerials = new Set();
  /** @type {Map<string, number>} */
  const pendingQtyBySku = new Map();

  const cartState = {
    data: null,
    ts: 0,
    pending: null,
    /** Monotonic generation so older /cart.js responses cannot overwrite newer state. */
    generation: 0,
    /** Last generation successfully applied to cartState.data. */
    appliedGeneration: 0,
    ready: false,
    error: null,
    /** Monotonic cart-mutation counter advanced on successful add/remove/change. */
    mutationGeneration: 0,
  };

  function normalizeId(value) {
    if (value === null || value === undefined) return '';
    const stringValue = String(value).trim();
    if (!stringValue) return '';
    if (stringValue.includes('/')) return stringValue.split('/').pop() || '';
    return stringValue;
  }

  function normalizeSku(value) {
    return String(value || '')
      .trim()
      .toUpperCase();
  }

  /**
   * Normalize products-batch payload → products list + SKU map.
   * Accepts either the full API envelope or `data.products` array.
   * @param {unknown} response
   * @returns {{ products: any[], skuMap: Map<string, any> }}
   */
  function normalizeWmsProductBatchResponse(response) {
    const envelope =
      response && typeof response === 'object' ? /** @type {Record<string, unknown>} */ (response) : null;
    const data =
      envelope && envelope.data && typeof envelope.data === 'object'
        ? /** @type {Record<string, unknown>} */ (envelope.data)
        : envelope;
    const products = Array.isArray(data?.products)
      ? data.products
      : Array.isArray(envelope?.products)
        ? envelope.products
        : Array.isArray(response)
          ? response
          : [];

    /** @type {Map<string, any>} */
    const skuMap = new Map();

    products.forEach((rawProduct) => {
      const normalized = normalizeProductNode(rawProduct);
      if (!normalized) return;
      const skuEntries = normalized.skuMap || Object.create(null);
      Object.keys(skuEntries).forEach((key) => {
        const skuItem = skuEntries[key];
        if (!skuItem) return;
        const mapKey = normalizeSku(skuItem.sku || key);
        if (!mapKey) return;
        skuMap.set(mapKey, skuItem);
      });
    });

    return { products, skuMap };
  }

  /**
   * Raw-WMS-only status (Coming Soon when WMS itself has zero).
   * Prefer getStorefrontStatus when cart-aware effective qty is known.
   * @param {unknown} quantity
   * @returns {'AVAILABLE'|'COMING_SOON'}
   */
  function getWmsStatus(quantity) {
    return Number(quantity) > 0 ? WMS_STATUS_AVAILABLE : WMS_STATUS_COMING_SOON;
  }

  /**
   * Cart-aware storefront status.
   * Coming Soon ONLY when raw WMS qty is zero.
   * @param {unknown} rawQuantity
   * @param {unknown} effectiveQuantity
   * @returns {'AVAILABLE'|'COMING_SOON'|'ALL_IN_CART'}
   */
  function getStorefrontStatus(rawQuantity, effectiveQuantity) {
    const raw = Math.max(0, Math.floor(Number(rawQuantity) || 0));
    const effective = Math.max(0, Math.floor(Number(effectiveQuantity) || 0));
    if (raw <= 0) return WMS_STATUS_COMING_SOON;
    if (effective <= 0) return WMS_STATUS_ALL_IN_CART;
    return WMS_STATUS_AVAILABLE;
  }

  /**
   * Shared matrix / row availability resolver.
   * Loading until both WMS and cart are resolved — never treat unresolved as zero.
   * Confirmed raw WMS zero → Coming Soon (never AVAILABILITY UNAVAILABLE).
   * @param {{
   *   rawWmsQty?: unknown,
   *   effectiveQty?: unknown,
   *   effectiveMinWeight?: unknown,
   *   isLoading?: boolean,
   *   isWmsLoading?: boolean,
   *   isCartLoading?: boolean,
   *   wmsResolved?: boolean,
   *   cartResolved?: boolean,
   *   wmsRequestFailed?: boolean,
   *   cartError?: boolean
   * }} input
   */
  function resolveVariantAvailabilityState(input) {
    const args = input || {};
    const wmsResolved =
      args.wmsResolved != null
        ? Boolean(args.wmsResolved)
        : args.isLoading !== true && args.isWmsLoading !== true && args.wmsRequestFailed !== true;
    const cartResolved =
      args.cartResolved != null ? Boolean(args.cartResolved) : isCartStateReady();
    const isWmsLoading =
      args.isLoading === true || args.isWmsLoading === true || wmsResolved === false;
    const isCartLoading = args.isCartLoading === true || cartResolved === false;

    if (isWmsLoading || isCartLoading) {
      return {
        state: 'loading',
        label: LABEL_CHECKING,
        availableQuantity: null,
        rawAvailableCount: null,
        minimumWeight: null,
        minimumWeightLabel: '',
        quantityEnabled: false,
        weightSelectionEnabled: false,
        addToCartEnabled: false,
        renderSkeleton: true,
      };
    }

    if (args.wmsRequestFailed === true || args.cartError === true) {
      return {
        state: 'error',
        label: 'UNAVAILABLE',
        availableQuantity: null,
        rawAvailableCount: null,
        minimumWeight: null,
        minimumWeightLabel: 'Min wt: N/A',
        quantityEnabled: false,
        weightSelectionEnabled: false,
        addToCartEnabled: false,
        renderSkeleton: false,
      };
    }

    // Normalize quantities only after loading/error gates — avoids Coming Soon flash.
    const rawQuantity = Number.isFinite(Number(args.rawWmsQty))
      ? Math.max(0, Math.floor(Number(args.rawWmsQty)))
      : 0;
    const remainingQuantity = Number.isFinite(Number(args.effectiveQty))
      ? Math.max(0, Math.floor(Number(args.effectiveQty)))
      : 0;
    const storefrontStatus = getStorefrontStatus(rawQuantity, remainingQuantity);
    const minWeight = toPositiveNumberOrNull(args.effectiveMinWeight);

    if (storefrontStatus === WMS_STATUS_COMING_SOON) {
      return {
        state: 'coming-soon',
        label: LABEL_COMING_SOON,
        availableQuantity: 0,
        rawAvailableCount: rawQuantity,
        minimumWeight: null,
        minimumWeightLabel: 'Min wt: N/A',
        quantityEnabled: false,
        weightSelectionEnabled: false,
        addToCartEnabled: false,
        renderSkeleton: false,
      };
    }

    if (storefrontStatus === WMS_STATUS_ALL_IN_CART) {
      return {
        state: 'all-in-cart',
        label: 'AVAILABLE QTY: 0',
        availableQuantity: 0,
        rawAvailableCount: rawQuantity,
        minimumWeight: null,
        minimumWeightLabel: 'Min wt: N/A',
        quantityEnabled: false,
        weightSelectionEnabled: false,
        addToCartEnabled: false,
        renderSkeleton: false,
      };
    }

    return {
      state: 'available',
      label: `AVAILABLE QTY: ${remainingQuantity}`,
      availableQuantity: remainingQuantity,
      rawAvailableCount: rawQuantity,
      minimumWeight: minWeight,
      minimumWeightLabel: formatMinimumWeightLabel(minWeight),
      quantityEnabled: true,
      weightSelectionEnabled: true,
      addToCartEnabled: true,
      renderSkeleton: false,
    };
  }

  /**
   * Accessible skeleton markup for WMS-dependent fields. No temporary text.
   * @param {'min-weight'|'available-qty'|'weight-action'|'status'|'badge'} kind
   * @returns {string}
   */
  function getInventorySkeletonMarkup(kind) {
    const map = {
      'min-weight': {
        cls: 'aone-inventory-skeleton aone-inventory-skeleton--min-weight',
        label: 'Loading minimum weight',
      },
      'available-qty': {
        cls: 'aone-inventory-skeleton aone-inventory-skeleton--available-qty',
        label: 'Loading available quantity',
      },
      'weight-action': {
        cls: 'aone-inventory-skeleton aone-inventory-skeleton--weight-action',
        label: 'Loading weight selection',
      },
      status: {
        cls: 'aone-inventory-skeleton aone-inventory-skeleton--status',
        label: 'Loading availability',
      },
      badge: {
        cls: 'aone-inventory-skeleton aone-inventory-skeleton--badge',
        label: 'Loading availability',
      },
    };
    const entry = map[kind] || map.status;
    return (
      `<span class="${entry.cls}" aria-hidden="true"></span>` +
      `<span class="visually-hidden">${entry.label}</span>`
    );
  }

  /**
   * Matrix / row minimum-weight label. Uses N/A when weight is missing or non-positive.
   * @param {unknown} value
   * @param {{ unit?: string, precision?: number }} [options]
   * @returns {string}
   */
  function formatMinimumWeightLabel(value, options) {
    const opts = options || {};
    const parsedWeight = Number(value);
    if (!Number.isFinite(parsedWeight) || parsedWeight <= 0) {
      return 'Min wt: N/A';
    }
    const precision = Number.isFinite(Number(opts.precision)) ? Number(opts.precision) : 3;
    const unit = String(opts.unit || 'g').trim() || 'g';
    const trimmed = parsedWeight.toFixed(precision).replace(/\.?0+$/, '');
    return `Min wt: ${trimmed}${unit}`;
  }

  /**
   * Weight button UI resolver shared by Quick View and PDP matrix.
   * Callers should use loading skeletons (label '' + disabled true) while WMS is pending.
   *
   * @param {{
   *   isLoading?: boolean,
   *   isAvailable?: boolean,
   *   effectiveAvailableQuantity?: unknown,
   *   minimumWeight?: unknown,
   *   minimumWeightLabel?: unknown,
   *   selectedSerialNumbers?: unknown,
   *   unit?: string,
   *   precision?: number
   * }} input
   * @returns {{ state: 'loading'|'coming-soon'|'minimum-weight'|'edit-weight', label: string, disabled: boolean }}
   */
  function resolveWeightButtonState(input) {
    const args = input || {};
    const isLoading = args.isLoading === true;
    if (isLoading) return { state: 'loading', label: '', disabled: true };

    const qty =
      args.effectiveAvailableQuantity == null
        ? null
        : Math.max(0, Math.floor(Number(args.effectiveAvailableQuantity) || 0));

    const selectedCount = (() => {
      const selected = args.selectedSerialNumbers;
      if (Array.isArray(selected)) {
        return selected.map((value) => normalizeSerialNumber(value)).filter(Boolean).length;
      }
      if (selected instanceof Set) {
        let c = 0;
        selected.forEach((value) => {
          if (normalizeSerialNumber(value)) c += 1;
        });
        return c;
      }
      return parseSerialNumbers(selected).length;
    })();

    // Coming Soon only when effective availability is zero — never when stock text is positive.
    const isAvailable = args.isAvailable === true || (qty != null ? qty > 0 : false);
    if (args.isAvailable === false || !isAvailable || (qty != null && qty <= 0)) {
      return { state: 'coming-soon', label: 'Coming Soon', disabled: true };
    }

    if (selectedCount > 0) return { state: 'edit-weight', label: 'Edit weight', disabled: false };

    const minWeight =
      args.minimumWeight != null
        ? args.minimumWeight
        : Number.isFinite(Number(args.minimumWeightLabel))
          ? Number(args.minimumWeightLabel)
          : null;
    const hasValidMinWeight = Number.isFinite(Number(minWeight)) && Number(minWeight) > 0;
    const computedLabel =
      args.minimumWeightLabel != null &&
      String(args.minimumWeightLabel).trim() &&
      !String(args.minimumWeightLabel).includes('N/A')
        ? String(args.minimumWeightLabel)
        : hasValidMinWeight
          ? formatMinimumWeightLabel(minWeight, { unit: args.unit, precision: args.precision })
          : '';

    if (hasValidMinWeight && computedLabel) {
      return { state: 'minimum-weight', label: computedLabel, disabled: false };
    }

    // Effective qty > 0 but min weight missing — keep selectable, never contradict stock with Coming Soon.
    return { state: 'select-weight', label: 'Select Weight', disabled: false };
  }

  /**
   * Canonical cart-aware availability for one variant/SKU.
   * Reuses getSkuAvailability / getEffectiveSkuInventory — do not duplicate subtraction elsewhere.
   * @param {{
   *   productId?: unknown,
   *   variantId?: unknown,
   *   sku?: unknown,
   *   rawWmsOptions?: unknown,
   *   keepSerials?: Set<string> | string[] | null,
   *   excludeLineKey?: string | null
   * }} input
   */
  function calculateEffectiveAvailability(input) {
    const args = input || {};
    const variantId = normalizeId(args.variantId);
    const registration = variantId ? getVariantRegistration(variantId) : null;
    const productId = normalizeId(args.productId) || (registration ? registration.productId : '');
    const sku = normalizeSku(args.sku) || (registration ? registration.sku : '');

    if (productId && sku) {
      const availability = getSkuAvailability(productId, sku, {
        keepSerials: args.keepSerials,
        excludeLineKey: args.excludeLineKey,
      });
      if (availability.loading || availability.status === STATUS_LOADING || availability.status === STATUS_IDLE) {
        return {
          loading: true,
          rawAvailableQty: null,
          reservedInCartQty: null,
          effectiveAvailableQty: null,
          availableOptions: [],
          minAvailableWeight: null,
          storefrontStatus: '',
          productId,
          sku,
          variantId,
        };
      }
      const rawAvailableQty = Math.max(0, Math.floor(Number(availability.rawAvailableCount) || 0));
      const effectiveAvailableQty = Math.max(
        0,
        Math.floor(Number(availability.effectiveAvailableCount ?? availability.count) || 0)
      );
      return {
        loading: false,
        rawAvailableQty,
        reservedInCartQty: Math.max(0, rawAvailableQty - effectiveAvailableQty),
        effectiveAvailableQty,
        availableOptions: Array.isArray(availability.availableOptions) ? availability.availableOptions : [],
        minAvailableWeight: availability.minWeight ?? null,
        storefrontStatus: availability.storefrontStatus || getStorefrontStatus(rawAvailableQty, effectiveAvailableQty),
        productId,
        sku,
        variantId,
      };
    }

    const rawOptions = Array.isArray(args.rawWmsOptions) ? args.rawWmsOptions : [];
    const cartSerialSet = getCartSerialSet({
      keepSerials: args.keepSerials,
      excludeLineKey: args.excludeLineKey,
    });
    const effective = getEffectiveSkuInventory(
      { availableOptions: rawOptions, count: rawOptions.length },
      cartSerialSet,
      { keepSerials: args.keepSerials }
    );
    const rawAvailableQty = Math.max(0, Math.floor(Number(effective.rawAvailableCount) || 0));
    const effectiveAvailableQty = Math.max(
      0,
      Math.floor(Number(effective.effectiveAvailableCount) || 0)
    );
    return {
      loading: !isCartStateReady(),
      rawAvailableQty,
      reservedInCartQty: Math.max(0, rawAvailableQty - effectiveAvailableQty),
      effectiveAvailableQty,
      availableOptions: Array.isArray(effective.availableOptions) ? effective.availableOptions : [],
      minAvailableWeight: effective.minWeight ?? null,
      storefrontStatus: effective.storefrontStatus || getStorefrontStatus(rawAvailableQty, effectiveAvailableQty),
      productId,
      sku,
      variantId,
    };
  }

  /**
   * Shared Select Weight / Edit Weight label for Order Sheet, Quick View, and PDP.
   * Loading returns null so callers can render a skeleton instead of actionable text.
   * @param {{
   *   quantity?: unknown,
   *   selectedSerialNumbers?: unknown,
   *   isLoading?: boolean,
   *   isAvailable?: boolean
   * }} input
   * @returns {string | null}
   */
  function resolveWeightActionLabel(input) {
    const args = input || {};
    if (args.isLoading === true) return null;
    if (args.isAvailable === false) return 'Select Weight';

    const qty = Math.max(0, Math.floor(Number(args.quantity) || 0));
    let selectedCount = 0;
    const selected = args.selectedSerialNumbers;
    if (Array.isArray(selected)) {
      selectedCount = selected.map((value) => normalizeSerialNumber(value)).filter(Boolean).length;
    } else if (selected instanceof Set) {
      selected.forEach((value) => {
        if (normalizeSerialNumber(value)) selectedCount += 1;
      });
    } else {
      selectedCount = parseSerialNumbers(selected).length;
    }

    return qty > 0 && selectedCount > 0 ? 'Edit Weight' : 'Select Weight';
  }

  /**
   * @param {unknown} value
   * @returns {string}
   */
  function normalizeSerialNumber(value) {
    return String(value || '').trim();
  }

  /** @deprecated Use normalizeSerialNumber */
  function normalizeSerial(value) {
    return normalizeSerialNumber(value);
  }

  /**
   * Parse SerialNumbers from cart line properties (single, CSV, array, JSON array).
   * Exact trim match — does not uppercase (serials are case-sensitive unless WMS says otherwise).
   * @param {unknown} value
   * @returns {string[]}
   */
  function parseSerialNumbers(value) {
    if (Array.isArray(value)) {
      return value
        .flatMap(parseSerialNumbers)
        .map(normalizeSerialNumber)
        .filter(Boolean);
    }
    const raw = String(value || '').trim();
    if (!raw) return [];
    if (raw.startsWith('[')) {
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          return parsed
            .flatMap(parseSerialNumbers)
            .map(normalizeSerialNumber)
            .filter(Boolean);
        }
      } catch (_error) {
        /* comma-separated fallback */
      }
    }
    return raw
      .split(',')
      .map(normalizeSerialNumber)
      .filter(Boolean);
  }

  /** @deprecated Use parseSerialNumbers */
  function parseSerialList(value) {
    return parseSerialNumbers(value);
  }

  /**
   * Rebuild weight groups from remaining serial options (by numeric weight label).
   * @param {Array<{ weight?: unknown, unit?: unknown, serialNumber?: unknown }>} options
   * @returns {Array<{ weight: number, unit: string, count: number, options: typeof options }>}
   */
  function buildWeightGroupsFromOptions(options) {
    const list = Array.isArray(options) ? options : [];
    /** @type {Map<string, { weight: number, unit: string, count: number, options: any[] }>} */
    const map = new Map();
    list.forEach((option) => {
      if (!option || typeof option !== 'object') return;
      const weight = toPositiveNumberOrNull(
        /** @type {Record<string, unknown>} */ (option).weight ??
          /** @type {Record<string, unknown>} */ (option).value
      );
      if (weight == null) return;
      const unit =
        String(/** @type {Record<string, unknown>} */ (option).unit || 'g').trim() || 'g';
      const key = `${weight}|${unit}`;
      if (!map.has(key)) {
        map.set(key, { weight, unit, count: 0, options: [] });
      }
      const group = map.get(key);
      if (!group) return;
      group.count += 1;
      group.options.push(option);
    });
    return Array.from(map.values()).sort((a, b) => a.weight - b.weight);
  }

  /**
   * Filter WMS availableOptions by cart serials and rebuild derived fields.
   * Does not mutate the original WMS SKU record.
   * @param {Record<string, unknown> | null | undefined} wmsSku
   * @param {Set<string> | null | undefined} cartSerialSet
   * @param {{ keepSerials?: Set<string> | string[] | null }} [options]
   */
  function getEffectiveSkuInventory(wmsSku, cartSerialSet, options) {
    const opts = options || {};
    const rawOptions = Array.isArray(wmsSku?.availableOptions)
      ? /** @type {any[]} */ (wmsSku.availableOptions)
      : Array.isArray(wmsSku?.availableWeights)
        ? /** @type {any[]} */ (wmsSku.availableWeights)
        : [];

    /** @type {Set<string>} */
    const keepSerials = new Set();
    if (opts.keepSerials instanceof Set) {
      opts.keepSerials.forEach((serial) => {
        const key = normalizeSerialNumber(serial);
        if (key) keepSerials.add(key);
      });
    } else if (Array.isArray(opts.keepSerials)) {
      opts.keepSerials.forEach((serial) => {
        const key = normalizeSerialNumber(serial);
        if (key) keepSerials.add(key);
      });
    }

    const excludedSerials = cartSerialSet instanceof Set ? cartSerialSet : new Set();

    const effectiveOptions = rawOptions.filter((option) => {
      if (!option || typeof option !== 'object') return false;
      const serial = normalizeSerialNumber(
        /** @type {Record<string, unknown>} */ (option).serialNumber ??
          /** @type {Record<string, unknown>} */ (option).serial
      );
      if (!serial) return false;
      if (keepSerials.has(serial)) return true;
      return !excludedSerials.has(serial);
    });

    const sortedOptions = [...effectiveOptions].sort((a, b) => {
      const weightA = Number(a?.weight);
      const weightB = Number(b?.weight);
      const weightDifference =
        (Number.isFinite(weightA) ? weightA : 0) - (Number.isFinite(weightB) ? weightB : 0);
      if (weightDifference !== 0) return weightDifference;
      return normalizeSerialNumber(a?.serialNumber ?? a?.serial).localeCompare(
        normalizeSerialNumber(b?.serialNumber ?? b?.serial)
      );
    });

    const effectiveCount = sortedOptions.length;
    const rawCountFromOptions = rawOptions.filter((option) => {
      if (!option || typeof option !== 'object') return false;
      return Boolean(
        normalizeSerialNumber(
          /** @type {Record<string, unknown>} */ (option).serialNumber ??
            /** @type {Record<string, unknown>} */ (option).serial
        )
      );
    }).length;
    const declaredCount = toWholeNonNegative(
      wmsSku?.count ?? wmsSku?.availableCount ?? wmsSku?.availableQuantity ?? wmsSku?.quantity
    );
    const rawAvailableCount =
      rawCountFromOptions > 0
        ? rawCountFromOptions
        : declaredCount != null
          ? declaredCount
          : 0;

    return {
      ...(wmsSku && typeof wmsSku === 'object' ? wmsSku : {}),
      rawAvailableCount,
      rawAvailableOptions: rawOptions,
      availableOptions: sortedOptions,
      count: effectiveCount,
      effectiveAvailableCount: effectiveCount,
      minWeight: effectiveCount ? toPositiveNumberOrNull(sortedOptions[0].weight) : null,
      maxWeight: effectiveCount
        ? toPositiveNumberOrNull(sortedOptions[sortedOptions.length - 1].weight)
        : null,
      weightGroups: buildWeightGroupsFromOptions(sortedOptions),
      storefrontStatus: getStorefrontStatus(rawAvailableCount, effectiveCount),
    };
  }

  /**
   * Rebuild product-level summary from effective SKU inventories.
   * @param {any} productEntry
   * @param {Set<string> | null | undefined} cartSerialSet
   * @param {{ keepSerials?: Set<string> | string[] | null }} [options]
   */
  function getEffectiveProductInventory(productEntry, cartSerialSet, options) {
    if (!productEntry) return null;
    const skuMap = productEntry.skuMap || Object.create(null);
    const skuKeys = Object.keys(skuMap);
    /** @type {Record<string, any>} */
    const effectiveSkuMap = Object.create(null);
    let effectiveTotal = 0;
    let rawTotal = 0;
    /** @type {number | null} */
    let minWeight = null;
    /** @type {number | null} */
    let maxWeight = null;
    /** @type {any[]} */
    const allEffectiveOptions = [];

    skuKeys.forEach((key) => {
      const rawSku = skuMap[key];
      if (!rawSku) return;
      const effective = getEffectiveSkuInventory(rawSku, cartSerialSet, options);
      effectiveSkuMap[key] = effective;
      effectiveTotal += Math.max(0, Math.floor(Number(effective.effectiveAvailableCount) || 0));
      rawTotal += Math.max(0, Math.floor(Number(effective.rawAvailableCount) || 0));
      if (Array.isArray(effective.availableOptions)) {
        allEffectiveOptions.push(...effective.availableOptions);
      }
      const skuMin = toPositiveNumberOrNull(effective.minWeight);
      const skuMax = toPositiveNumberOrNull(effective.maxWeight);
      if (skuMin != null) minWeight = minWeight == null ? skuMin : Math.min(minWeight, skuMin);
      if (skuMax != null) maxWeight = maxWeight == null ? skuMax : Math.max(maxWeight, skuMax);
    });

    // Prefer summing raw option counts; fall back to stored WMS product total.
    const storedRaw =
      toWholeNonNegative(productEntry.rawWmsTotalAvailableCount ?? productEntry.totalAvailableCount) ?? 0;
    if (rawTotal <= 0 && storedRaw > 0 && skuKeys.length === 0) {
      rawTotal = storedRaw;
    } else if (rawTotal <= 0 && storedRaw > 0 && effectiveTotal === 0 && skuKeys.length > 0) {
      // SKUs present but no serial options — trust stored raw for Coming Soon decisions.
      const skuRawSum = skuKeys.reduce((sum, key) => {
        const count = toWholeNonNegative(skuMap[key]?.count);
        return sum + (count || 0);
      }, 0);
      rawTotal = skuRawSum > 0 ? skuRawSum : storedRaw;
    }

    return {
      productId: productEntry.productId,
      status: productEntry.status,
      errorCode: productEntry.errorCode || '',
      skuMap: effectiveSkuMap,
      rawWmsTotalAvailableCount: rawTotal > 0 ? rawTotal : storedRaw,
      totalAvailableCount: effectiveTotal,
      effectiveAvailableCount: effectiveTotal,
      minWeight: effectiveTotal > 0 ? minWeight : null,
      maxWeight: effectiveTotal > 0 ? maxWeight : null,
      weightGroups: buildWeightGroupsFromOptions(allEffectiveOptions),
      storefrontStatus: getStorefrontStatus(rawTotal > 0 ? rawTotal : storedRaw, effectiveTotal),
    };
  }

  /**
   * @param {string} sku
   * @param {Record<string, unknown> | null | undefined} record
   */
  function normalizeInventoryRecord(sku, record) {
    const safeSku = normalizeSku(sku);
    const quantity = Math.max(
      0,
      Number(
        record?.count ??
          record?.availableQuantity ??
          record?.availableCount ??
          record?.quantity ??
          record?.qty ??
          0
      ) || 0
    );
    const status = getWmsStatus(quantity);
    const availableOptions = Array.isArray(record?.availableOptions)
      ? record.availableOptions
      : Array.isArray(record?.availableWeights)
        ? record.availableWeights
        : [];
    return {
      sku: safeSku,
      quantity,
      status,
      available: status === WMS_STATUS_AVAILABLE,
      minimumWeight: toPositiveNumberOrNull(record?.minWeight ?? record?.minimumWeight),
      maximumWeight: toPositiveNumberOrNull(record?.maxWeight ?? record?.maximumWeight),
      weightGroups: Array.isArray(record?.weightGroups) ? record.weightGroups : [],
      availableOptions,
      syncedAt: record?.syncedAt || record?.synchronizedAt || record?.updatedAt || null,
      found: record ? record.found !== false : false,
      productId: normalizeId(record?.productId),
    };
  }

  function emptyInventoryRecord(sku) {
    return normalizeInventoryRecord(sku, {
      count: 0,
      found: false,
      minWeight: null,
      maxWeight: null,
      weightGroups: [],
      availableOptions: [],
    });
  }

  function toNumber(value) {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string') {
      const match = value.replace(/,/g, '').match(/-?\d+(\.\d+)?/);
      if (!match) return null;
      const n = Number(match[0]);
      return Number.isFinite(n) ? n : null;
    }
    return null;
  }

  function toWholeNonNegative(value) {
    const n = toNumber(value);
    if (!Number.isFinite(n) || n === null) return null;
    return Math.max(0, Math.floor(n));
  }

  function toPositiveNumberOrNull(value) {
    const n = toNumber(value);
    if (!Number.isFinite(n) || n === null || n <= 0) return null;
    return n;
  }

  function ensureProductEntry(productId) {
    const id = normalizeId(productId);
    if (!id) return null;
    if (!productState.has(id)) {
      productState.set(id, {
        productId: id,
        status: STATUS_IDLE,
        totalAvailableCount: null,
        rawWmsTotalAvailableCount: null,
        minWeight: null,
        maxWeight: null,
        skuMap: Object.create(null),
        errorCode: '',
      });
    }
    return productState.get(id) || null;
  }

  function cloneEntry(entry) {
    if (!entry) return null;
    return {
      productId: entry.productId,
      status: entry.status,
      totalAvailableCount: entry.totalAvailableCount,
      rawWmsTotalAvailableCount: entry.rawWmsTotalAvailableCount,
      effectiveAvailableCount: entry.effectiveAvailableCount,
      minWeight: entry.minWeight,
      maxWeight: entry.maxWeight,
      skuMap: entry.skuMap,
      errorCode: entry.errorCode || '',
      storefrontStatus: entry.storefrontStatus,
    };
  }

  function notifyGlobal(detail) {
    if (!globalSubscribers.size) return;
    globalSubscribers.forEach((callback) => {
      try {
        callback(detail);
      } catch (_error) {
        /* avoid breaking other listeners */
      }
    });
  }

  function notifyProduct(productId) {
    const id = normalizeId(productId);
    if (!id) return;
    const listeners = subscribers.get(id);
    const snapshot = getProductAvailability(id);
    if (listeners && listeners.size) {
      listeners.forEach((callback) => {
        try {
          callback(snapshot);
        } catch (_error) {
          /* avoid breaking other listeners */
        }
      });
    }
    notifyGlobal({ type: 'product', productId: id, product: snapshot });
  }

  function warnDev(message, meta) {
    if (typeof console === 'undefined' || typeof console.warn !== 'function') return;
    if (meta !== undefined) {
      console.warn('[A-One WMS]', message, meta);
    } else {
      console.warn('[A-One WMS]', message);
    }
  }

  function normalizeSkuNode(rawSkuNode) {
    if (!rawSkuNode || typeof rawSkuNode !== 'object') return null;
    const skuNode = /** @type {Record<string, unknown>} */ (rawSkuNode);
    const sku = normalizeSku(skuNode.sku);
    if (!sku) return null;
    const found = skuNode.found !== false;
    const count = found
      ? toWholeNonNegative(
          skuNode.count ??
            skuNode.availableCount ??
            skuNode.availableQuantity ??
            skuNode.quantity ??
            skuNode.qty
        )
      : 0;
    const availableOptions = Array.isArray(skuNode.availableOptions)
      ? skuNode.availableOptions
      : Array.isArray(skuNode.availableWeights)
        ? skuNode.availableWeights
        : [];
    return {
      sku,
      found,
      count: count === null ? 0 : count,
      minWeight: toPositiveNumberOrNull(skuNode.minWeight ?? skuNode.minimumWeight),
      maxWeight: toPositiveNumberOrNull(skuNode.maxWeight ?? skuNode.maximumWeight),
      weightGroups: Array.isArray(skuNode.weightGroups) ? skuNode.weightGroups : [],
      availableOptions,
      syncedAt: skuNode.syncedAt || skuNode.synchronizedAt || skuNode.updatedAt || null,
    };
  }

  function normalizeProductNode(rawProductNode) {
    if (!rawProductNode || typeof rawProductNode !== 'object') return null;
    const productNode = /** @type {Record<string, unknown>} */ (rawProductNode);
    const productId = normalizeId(productNode.productId);
    if (!productId) return null;

    const summary =
      productNode.summary && typeof productNode.summary === 'object'
        ? /** @type {Record<string, unknown>} */ (productNode.summary)
        : {};
    const minWeightOption =
      summary.minimumWeightOption && typeof summary.minimumWeightOption === 'object'
        ? /** @type {Record<string, unknown>} */ (summary.minimumWeightOption)
        : null;

    // Prefer Product Batch field totalAvailableQuantity; legacy aliases are defensive only.
    const totalAvailableCount =
      toWholeNonNegative(
        summary.totalAvailableQuantity ?? summary.totalAvailableCount ?? summary.availableCount
      ) ?? 0;

    const minWeight = toPositiveNumberOrNull(
      summary.overallMinWeight ?? summary.minWeight ?? minWeightOption?.weight
    );
    const maxWeight = toPositiveNumberOrNull(
      summary.overallMaxWeight ?? summary.maxWeight ?? minWeightOption?.maxWeight
    );

    const skuList = Array.isArray(productNode.skus)
      ? productNode.skus
      : Array.isArray(productNode.items)
        ? productNode.items
        : [];

    /** @type {Record<string, any>} */
    const skuMap = Object.create(null);
    skuList.forEach((rawSku) => {
      const normalizedSku = normalizeSkuNode(rawSku);
      if (!normalizedSku) return;
      skuMap[normalizedSku.sku] = normalizedSku;
      skuToProductId.set(normalizedSku.sku, productId);

      const variants = Array.isArray(rawSku?.variants) ? rawSku.variants : [];
      variants.forEach((variantNode) => {
        if (!variantNode || typeof variantNode !== 'object') return;
        const variantObj = /** @type {Record<string, unknown>} */ (variantNode);
        const variantId = normalizeId(variantObj.variantId ?? variantObj.id);
        if (!variantId) return;
        registerVariant(variantId, productId, normalizedSku.sku);
      });
    });

    return {
      productId,
      found: productNode.found !== false,
      totalAvailableCount,
      minWeight,
      maxWeight,
      unit: summary.unit ?? minWeightOption?.unit ?? null,
      skuMap,
    };
  }

  function markProductLoading(productId) {
    const entry = ensureProductEntry(productId);
    if (!entry) return;
    entry.status = STATUS_LOADING;
    entry.errorCode = '';
    notifyProduct(entry.productId);
  }

  function markProductsLoading(productIds) {
    const ids = Array.isArray(productIds) ? productIds : [];
    ids.forEach((id) => markProductLoading(id));
  }

  function markProductError(productId, errorCode) {
    const entry = ensureProductEntry(productId);
    if (!entry) return;
    // Error is distinct from Coming Soon — UI must not claim WMS zero stock.
    entry.status = STATUS_ERROR;
    entry.totalAvailableCount = null;
    entry.rawWmsTotalAvailableCount = null;
    entry.effectiveAvailableCount = null;
    entry.minWeight = null;
    entry.maxWeight = null;
    entry.skuMap = Object.create(null);
    entry.storefrontStatus = '';
    entry.errorCode = String(errorCode || 'PRODUCT_UNAVAILABLE');
    notifyProduct(entry.productId);
  }

  function markProductsError(productIds, errorCode) {
    const ids = Array.isArray(productIds) ? productIds : [];
    ids.forEach((id) => markProductError(id, errorCode));
  }

  function upsertBatchProducts(products, requestedIds) {
    const list = Array.isArray(products) ? products : [];
    const requestIdSet = new Set((Array.isArray(requestedIds) ? requestedIds : []).map(normalizeId).filter(Boolean));
    const seen = new Set();

    list.forEach((rawProduct) => {
      const normalized = normalizeProductNode(rawProduct);
      if (!normalized) return;
      seen.add(String(normalized.productId));
      const entry = ensureProductEntry(normalized.productId);
      if (!entry) return;

      const quantity = Math.max(0, Math.floor(Number(normalized.totalAvailableCount) || 0));
      entry.status = STATUS_LOADED;
      // Store raw WMS totals unchanged. Effective values are derived at read time.
      entry.rawWmsTotalAvailableCount = quantity;
      entry.totalAvailableCount = quantity;
      entry.effectiveAvailableCount = quantity;
      entry.minWeight = quantity > 0 ? normalized.minWeight : null;
      entry.maxWeight = quantity > 0 ? normalized.maxWeight : null;
      entry.skuMap = normalized.skuMap || Object.create(null);
      entry.storefrontStatus = getWmsStatus(quantity);
      entry.errorCode = !normalized.found
        ? 'PRODUCT_NOT_FOUND'
        : quantity <= 0
          ? 'COMING_SOON'
          : '';

      notifyProduct(entry.productId);
    });

    requestIdSet.forEach((productId) => {
      if (seen.has(String(productId))) return;
      if (typeof console !== 'undefined' && typeof console.warn === 'function') {
        console.warn('[WMS availability] Missing product mapping', productId);
      }
      markProductError(productId, 'PRODUCT_MISSING_FROM_BATCH');
    });
  }

  /**
   * Cart-aware product availability. Raw WMS data stays in productState; callers receive derived values.
   * @param {string} productId
   * @param {{ keepSerials?: Set<string> | string[] | null, excludeLineKey?: string | null }} [options]
   */
  function getProductAvailability(productId, options) {
    const entry = ensureProductEntry(productId);
    if (!entry) return null;
    if (entry.status !== STATUS_LOADED) return cloneEntry(entry);
    if (!isCartStateReady() && options?.skipCartCheck !== true) {
      return {
        productId: entry.productId,
        status: STATUS_LOADING,
        errorCode: '',
        skuMap: entry.skuMap,
        rawWmsTotalAvailableCount: entry.rawWmsTotalAvailableCount,
        totalAvailableCount: null,
        effectiveAvailableCount: null,
        minWeight: null,
        maxWeight: null,
        storefrontStatus: '',
        loading: true,
      };
    }

    const cartSerialSet = getCartSerialSet(options);
    const effective = getEffectiveProductInventory(entry, cartSerialSet, options);
    if (!effective) return cloneEntry(entry);

    return {
      productId: entry.productId,
      status: entry.status,
      errorCode: entry.errorCode || '',
      skuMap: effective.skuMap,
      rawWmsTotalAvailableCount: effective.rawWmsTotalAvailableCount,
      totalAvailableCount: effective.totalAvailableCount,
      effectiveAvailableCount: effective.effectiveAvailableCount,
      minWeight: effective.minWeight,
      maxWeight: effective.maxWeight,
      weightGroups: effective.weightGroups,
      storefrontStatus: effective.storefrontStatus,
    };
  }

  /**
   * Cart-aware SKU availability.
   * @param {string} productId
   * @param {string} sku
   * @param {{ keepSerials?: Set<string> | string[] | null, excludeLineKey?: string | null }} [options]
   */
  function getSkuAvailability(productId, sku, options) {
    const entry = ensureProductEntry(productId);
    const safeSku = normalizeSku(sku);
    if (!entry) return { status: STATUS_ERROR, reason: 'PRODUCT_ID_MISSING', found: false, count: 0 };
    if (entry.status !== STATUS_LOADED) {
      return {
        status: entry.status,
        reason: entry.status === STATUS_ERROR ? entry.errorCode || 'PRODUCT_ERROR' : 'PRODUCT_NOT_READY',
        found: false,
        count: null,
        rawAvailableCount: null,
        effectiveAvailableCount: null,
        storefrontStatus: '',
        loading: entry.status === STATUS_LOADING || entry.status === STATUS_IDLE,
      };
    }
    if (!isCartStateReady() && options?.skipCartCheck !== true) {
      return {
        status: STATUS_LOADING,
        reason: 'CART_NOT_READY',
        found: false,
        count: null,
        rawAvailableCount: null,
        effectiveAvailableCount: null,
        storefrontStatus: '',
        loading: true,
      };
    }
    if (!safeSku) {
      return {
        status: STATUS_LOADED,
        reason: 'SKU_MISSING',
        found: false,
        count: 0,
        rawAvailableCount: 0,
        effectiveAvailableCount: 0,
        storefrontStatus: WMS_STATUS_COMING_SOON,
      };
    }
    const skuMap = entry.skuMap || Object.create(null);
    let skuEntry = skuMap[safeSku];
    if (!skuEntry && safeSku) {
      const fallbackKey = Object.keys(skuMap).find((key) => normalizeSku(key) === safeSku);
      if (fallbackKey) skuEntry = skuMap[fallbackKey];
    }
    if (!skuEntry) {
      return {
        status: STATUS_LOADED,
        reason: 'SKU_NOT_RETURNED',
        found: false,
        count: 0,
        rawAvailableCount: 0,
        effectiveAvailableCount: 0,
        storefrontStatus: WMS_STATUS_COMING_SOON,
      };
    }

    const cartSerialSet = getCartSerialSet(options);
    const effective = getEffectiveSkuInventory(skuEntry, cartSerialSet, options);
    const rawCount = Math.max(0, Math.floor(Number(effective.rawAvailableCount) || 0));
    const effectiveCount = Math.max(0, Math.floor(Number(effective.effectiveAvailableCount) || 0));

    return {
      status: STATUS_LOADED,
      found: skuEntry.found === true,
      count: effectiveCount,
      rawAvailableCount: rawCount,
      effectiveAvailableCount: effectiveCount,
      minWeight: effective.minWeight,
      maxWeight: effective.maxWeight,
      weightGroups: effective.weightGroups,
      availableOptions: effective.availableOptions,
      rawAvailableOptions: effective.rawAvailableOptions,
      syncedAt: skuEntry.syncedAt || null,
      storefrontStatus: effective.storefrontStatus,
    };
  }

  function resolveProductIdForSku(sku) {
    const safeSku = normalizeSku(sku);
    if (!safeSku) return '';
    if (skuToProductId.has(safeSku)) return skuToProductId.get(safeSku) || '';
    for (const [productId, entry] of productState.entries()) {
      if (entry?.skuMap && entry.skuMap[safeSku]) {
        skuToProductId.set(safeSku, productId);
        return productId;
      }
    }
    return '';
  }

  function getInventoryBySku(sku, options) {
    const safeSku = normalizeSku(sku);
    if (!safeSku) return emptyInventoryRecord('');
    const productId = resolveProductIdForSku(safeSku);
    if (!productId) return emptyInventoryRecord(safeSku);
    const product = ensureProductEntry(productId);
    if (!product || product.status === STATUS_LOADING || product.status === STATUS_IDLE) {
      return {
        ...emptyInventoryRecord(safeSku),
        productId,
        loading: true,
        status: '',
        available: false,
        quantity: null,
        rawAvailableCount: null,
        effectiveAvailableCount: null,
      };
    }
    if (!isCartStateReady() && options?.skipCartCheck !== true) {
      return {
        ...emptyInventoryRecord(safeSku),
        productId,
        loading: true,
        status: '',
        available: false,
        quantity: null,
        rawAvailableCount: null,
        effectiveAvailableCount: null,
      };
    }
    if (product.status === STATUS_ERROR) {
      return {
        ...emptyInventoryRecord(safeSku),
        productId,
        status: '',
        available: false,
        quantity: null,
        error: true,
        errorCode: product.errorCode || 'PRODUCT_ERROR',
      };
    }
    const skuAvailability = getSkuAvailability(productId, safeSku, options);
    if (skuAvailability.loading || skuAvailability.status === STATUS_LOADING) {
      return {
        ...emptyInventoryRecord(safeSku),
        productId,
        loading: true,
        status: '',
        available: false,
        quantity: null,
        rawAvailableCount: null,
        effectiveAvailableCount: null,
      };
    }
    const rawCount = Math.max(0, Math.floor(Number(skuAvailability.rawAvailableCount) || 0));
    const effectiveCount = Math.max(0, Math.floor(Number(skuAvailability.count) || 0));
    const storefrontStatus =
      skuAvailability.storefrontStatus || getStorefrontStatus(rawCount, effectiveCount);
    const record = normalizeInventoryRecord(safeSku, {
      ...skuAvailability,
      productId,
      count: effectiveCount,
      found: skuAvailability.found,
    });
    return {
      ...record,
      quantity: effectiveCount,
      rawAvailableCount: rawCount,
      effectiveAvailableCount: effectiveCount,
      status: storefrontStatus,
      available: storefrontStatus === WMS_STATUS_AVAILABLE,
      storefrontStatus,
      minimumWeight: skuAvailability.minWeight,
      maximumWeight: skuAvailability.maxWeight,
      weightGroups: skuAvailability.weightGroups,
      availableOptions: skuAvailability.availableOptions,
      rawAvailableOptions: skuAvailability.rawAvailableOptions,
    };
  }

  function getAvailableQuantity(sku) {
    return getInventoryBySku(sku).quantity;
  }

  function getStatus(sku) {
    return getInventoryBySku(sku).status;
  }

  function isAvailable(sku) {
    return getInventoryBySku(sku).available === true;
  }

  function getAvailableOptions(sku) {
    const inventory = getInventoryBySku(sku);
    return Array.isArray(inventory.availableOptions) ? inventory.availableOptions : [];
  }

  function getAvailableWeights(sku) {
    return getAvailableOptions(sku)
      .map((option) => {
        if (typeof option === 'number') return option;
        if (!option || typeof option !== 'object') return null;
        return toPositiveNumberOrNull(
          /** @type {Record<string, unknown>} */ (option).weight ??
            /** @type {Record<string, unknown>} */ (option).value
        );
      })
      .filter((weight) => weight != null);
  }

  function getMinimumWeight(sku) {
    return getInventoryBySku(sku).minimumWeight;
  }

  function isProductAvailable(productId) {
    const product = getProductAvailability(productId);
    return Boolean(
      product &&
        product.status === STATUS_LOADED &&
        Number.isFinite(product.totalAvailableCount) &&
        Number(product.totalAvailableCount) > 0
    );
  }

  function isSkuAvailable(productId, sku) {
    const availability = getSkuAvailability(productId, sku);
    return availability.status === STATUS_LOADED && availability.found === true && availability.count > 0;
  }

  function getSkuAvailableQuantity(productId, sku) {
    const availability = getSkuAvailability(productId, sku);
    if (availability.status !== STATUS_LOADED || availability.found !== true) return 0;
    return Math.max(0, Math.floor(Number(availability.count) || 0));
  }

  function subscribeToProductAvailability(productId, callback) {
    const id = normalizeId(productId);
    if (!id || typeof callback !== 'function') return function noop() {};
    if (!subscribers.has(id)) subscribers.set(id, new Set());
    const set = subscribers.get(id);
    set.add(callback);
    callback(getProductAvailability(id));
    return function unsubscribe() {
      const current = subscribers.get(id);
      if (!current) return;
      current.delete(callback);
      if (!current.size) subscribers.delete(id);
    };
  }

  function registerVariant(variantId, productId, sku) {
    const id = normalizeId(variantId);
    const normalizedProductId = normalizeId(productId);
    const normalizedSku = normalizeSku(sku);
    if (!id || !normalizedProductId || !normalizedSku) return;
    variantLookup.set(id, { productId: normalizedProductId, sku: normalizedSku });
  }

  function getVariantRegistration(variantId) {
    const id = normalizeId(variantId);
    if (!id) return null;
    return variantLookup.get(id) || null;
  }

  function getFormProductId(form) {
    const fromDataset = normalizeId(form?.dataset?.productId);
    if (fromDataset) return fromDataset;
    const host = form?.closest?.('[data-product-id]');
    return normalizeId(host?.dataset?.productId);
  }

  function getFormVariantId(form) {
    const field = form?.querySelector?.('input[name="id"]');
    return normalizeId(field && 'value' in field ? field.value : '');
  }

  function getFormRequestedQty(form) {
    const field = form?.querySelector?.('input[name="quantity"]');
    const qty = toWholeNonNegative(field && 'value' in field ? field.value : '');
    return qty === null || qty <= 0 ? 1 : qty;
  }

  function getCartJsUrl() {
    const root = window.Shopify?.routes?.root || '/';
    return `${root}cart.js`.replace(/\/{2,}cart\.js$/, '/cart.js');
  }

  async function fetchCart(forceRefresh) {
    const now = Date.now();
    const cacheIsFresh = now - cartState.ts < 2500;
    if (!forceRefresh && cacheIsFresh && cartState.data && cartState.ready) {
      return cartState.data;
    }
    // Reuse in-flight only for non-forced reads. Forced refreshes must not reuse a stale request.
    if (!forceRefresh && cartState.pending) return cartState.pending;

    const requestVersion = ++cartState.generation;
    const request = fetch(getCartJsUrl(), {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
      .then((response) => {
        if (!response.ok) throw new Error('cart_fetch_failed');
        return response.json();
      })
      .then((cart) => {
        if (requestVersion !== cartState.generation) {
          // Newer request owns the write. Prefer its result when still in flight.
          if (cartState.pending && cartState.pending !== request) {
            return cartState.pending;
          }
          return cartState.data;
        }
        cartState.data = cart;
        cartState.ts = Date.now();
        cartState.ready = true;
        cartState.error = null;
        cartState.appliedGeneration = requestVersion;
        syncReservedSerialsFromCart(cart);
        return cart;
      })
      .catch((error) => {
        if (requestVersion === cartState.generation) {
          cartState.error = error;
          // Keep previous ready cart if we had one; otherwise leave unresolved.
          if (!cartState.data) cartState.ready = false;
        }
        if (typeof console !== 'undefined' && typeof console.warn === 'function') {
          console.warn('[AONE Inventory] Cart refresh failed', error);
        }
        throw error;
      })
      .finally(() => {
        if (cartState.pending === request) cartState.pending = null;
      });

    cartState.pending = request;
    return cartState.pending;
  }

  async function getCurrentCart(forceRefresh) {
    return fetchCart(Boolean(forceRefresh));
  }

  async function refreshCartState(options) {
    const opts = options && typeof options === 'object' ? options : {};
    return fetchCart(opts.force !== false);
  }

  /**
   * Apply a complete Shopify cart payload if it includes items[].
   * @param {any} cart
   * @param {{ reason?: string, notify?: boolean }} [options]
   * @returns {boolean}
   */
  function applyCart(cart, options) {
    const opts = options || {};
    if (!cart || !Array.isArray(cart.items)) return false;
    const generation = ++cartState.generation;
    cartState.data = cart;
    cartState.ts = Date.now();
    cartState.ready = true;
    cartState.error = null;
    cartState.appliedGeneration = generation;
    syncReservedSerialsFromCart(cart);
    if (opts.notify !== false) {
      notifyCartInventoryUpdated(cart, opts.reason || 'cart-apply');
    }
    return true;
  }

  function invalidateCart(reason) {
    cartState.data = null;
    cartState.ts = 0;
    cartState.ready = false;
    cartState.error = null;
    ++cartState.generation;
    if (reason) {
      notifyGlobal({ type: 'cart-invalidate', reason, generation: cartState.generation });
    }
  }

  /**
   * Synchronously mark cart unresolved and notify subscribers to render skeletons
   * before any async /cart.js refresh completes (pageshow / BFCache).
   * @param {string} [reason]
   */
  function markCartDependentInventoryLoading(reason) {
    const safeReason = reason || 'cart-loading';
    invalidateCart(safeReason);
    const detail = {
      cart: null,
      serialNumbers: [],
      cartSerialNumbers: [],
      generation: cartState.generation,
      mutationGeneration: cartState.mutationGeneration,
      reason: safeReason,
      loading: true,
      cartReady: false,
      updatedAt: Date.now(),
    };
    cartChangeSubscribers.forEach((callback) => {
      try {
        callback(detail);
      } catch (_error) {
        /* avoid breaking other listeners */
      }
    });
    notifyGlobal({ type: 'cart-loading', ...detail });
    productState.forEach((_entry, productId) => {
      notifyProduct(productId);
    });
    try {
      document.dispatchEvent(new CustomEvent(CART_INVENTORY_EVENT, { detail }));
    } catch (_error) {
      /* CustomEvent unavailable */
    }
    try {
      document.dispatchEvent(new CustomEvent(INVENTORY_STATE_EVENT, { detail }));
    } catch (_error) {
      /* CustomEvent unavailable */
    }
  }

  function incrementCartGeneration() {
    cartState.mutationGeneration += 1;
    return cartState.mutationGeneration;
  }

  /**
   * After a successful cart mutation: apply complete cart or force-refresh /cart.js.
   * @param {any} [responseCart]
   * @param {{ reason?: string }} [options]
   */
  async function handleSuccessfulCartMutation(responseCart, options) {
    const opts = options || {};
    const reason = opts.reason || 'cart-mutation';
    incrementCartGeneration();
    invalidateCart(reason);

    if (responseCart && Array.isArray(responseCart.items)) {
      applyCart(responseCart, { reason, notify: true });
      pendingSerials.clear();
      pendingQtyBySku.clear();
      return cartState.data;
    }

    const cart = await fetchCart(true);
    pendingSerials.clear();
    pendingQtyBySku.clear();
    notifyCartInventoryUpdated(cart, reason);
    return cart;
  }

  function getCartSkuQty(cart, sku) {
    const safeSku = normalizeSku(sku);
    if (!safeSku || !cart || !Array.isArray(cart.items)) return 0;
    return cart.items.reduce((sum, item) => {
      if (!item || typeof item !== 'object') return sum;
      const directSku = normalizeSku(item.sku);
      const propSku = normalizeSku(item.properties && item.properties.SKU);
      const lineSku = directSku || propSku;
      if (!lineSku) return sum;
      if (lineSku !== safeSku) return sum;
      const qty = toWholeNonNegative(item.quantity);
      return sum + (qty || 0);
    }, 0);
  }

  /**
   * Collect unique cart serials. For Edit Weight, pass excludeLineKey / keepSerials
   * so the current line's serials remain selectable.
   * @param {{
   *   cart?: any,
   *   excludeLineKey?: string | null,
   *   keepSerials?: Set<string> | string[] | null,
   *   includePending?: boolean
   * }} [options]
   * @returns {Set<string>}
   */
  function getCartSerialSet(options) {
    const opts = options || {};
    /** @type {Set<string>} */
    const serials = new Set();
    const cart = opts.cart || cartState.data;
    const excludeLineKey = String(opts.excludeLineKey || '').trim();

    /** @type {Set<string>} */
    const keepSerials = new Set();
    if (opts.keepSerials instanceof Set) {
      opts.keepSerials.forEach((value) => {
        const key = normalizeSerialNumber(value);
        if (key) keepSerials.add(key);
      });
    } else if (Array.isArray(opts.keepSerials)) {
      opts.keepSerials.forEach((value) => {
        const key = normalizeSerialNumber(value);
        if (key) keepSerials.add(key);
      });
    }

    if (cart && Array.isArray(cart.items)) {
      cart.items.forEach((item) => {
        if (!item || typeof item !== 'object') return;
        const lineKey = String(item.key || '').trim();
        if (excludeLineKey && lineKey && lineKey === excludeLineKey) return;
        const props = item.properties || {};
        parseSerialNumbers(props.SerialNumbers || props.serialNumbers || props.SerialNumber).forEach(
          (serial) => {
            if (keepSerials.has(serial)) return;
            serials.add(serial);
          }
        );
      });
    }

    if (opts.includePending !== false) {
      pendingSerials.forEach((serial) => {
        const key = normalizeSerialNumber(serial);
        if (!key || keepSerials.has(key)) return;
        serials.add(key);
      });
    }

    return serials;
  }

  /**
   * Edit-Weight exclusion set = all cart serials minus the current line's serials.
   * @param {string} lineKey
   * @param {any} [cart]
   * @returns {{ exclusionSet: Set<string>, keepSerials: Set<string> }}
   */
  function getEditWeightSerialContext(lineKey, cart) {
    const key = String(lineKey || '').trim();
    const source = cart || cartState.data;
    /** @type {Set<string>} */
    const keepSerials = new Set();
    if (key && source && Array.isArray(source.items)) {
      const line = source.items.find((item) => item && String(item.key || '').trim() === key);
      if (line) {
        const props = line.properties || {};
        parseSerialNumbers(props.SerialNumbers || props.serialNumbers || props.SerialNumber).forEach(
          (serial) => keepSerials.add(serial)
        );
      }
    }
    return {
      keepSerials,
      exclusionSet: getCartSerialSet({ cart: source, excludeLineKey: key, keepSerials, includePending: true }),
    };
  }

  function syncReservedSerialsFromCart(cart) {
    reservedSerials.clear();
    if (!cart || !Array.isArray(cart.items)) return;
    cart.items.forEach((item) => {
      if (!item || typeof item !== 'object') return;
      const props = item.properties || {};
      parseSerialNumbers(props.SerialNumbers || props.serialNumbers || props.SerialNumber).forEach(
        (serial) => reservedSerials.add(serial)
      );
    });
  }

  function getCartReservedQuantity(sku) {
    // Prefer serial-count reservation over numeric line qty when options exist.
    const safeSku = normalizeSku(sku);
    if (!safeSku) return 0;
    const productId = resolveProductIdForSku(safeSku);
    if (productId) {
      const entry = productState.get(productId);
      const skuEntry = entry?.skuMap?.[safeSku];
      if (skuEntry && Array.isArray(skuEntry.availableOptions) && skuEntry.availableOptions.length) {
        const cartSerials = getCartSerialSet({ includePending: false });
        let reserved = 0;
        skuEntry.availableOptions.forEach((option) => {
          const serial = normalizeSerialNumber(option?.serialNumber ?? option?.serial);
          if (serial && cartSerials.has(serial)) reserved += 1;
        });
        return reserved;
      }
    }
    return getCartSkuQty(cartState.data, sku);
  }

  function getPendingQuantity(sku) {
    const safeSku = normalizeSku(sku);
    if (!safeSku) return 0;
    return Math.max(0, Math.floor(Number(pendingQtyBySku.get(safeSku) || 0)));
  }

  function getRemainingQuantity(sku, options) {
    const inventory = getInventoryBySku(sku, options);
    const effective = Math.max(0, Math.floor(Number(inventory.effectiveAvailableCount ?? inventory.quantity) || 0));
    const pending = getPendingQuantity(sku);
    // Effective count already excludes cart serials; only subtract in-flight pending qty.
    return Math.max(0, effective - pending);
  }

  function reservePendingQuantity(sku, qty) {
    const safeSku = normalizeSku(sku);
    const amount = Math.max(0, toWholeNonNegative(qty) || 0);
    if (!safeSku || amount <= 0) return;
    pendingQtyBySku.set(safeSku, getPendingQuantity(safeSku) + amount);
    notifyGlobal({ type: 'pending-qty', sku: safeSku, pending: getPendingQuantity(safeSku) });
  }

  function releasePendingQuantity(sku, qty) {
    const safeSku = normalizeSku(sku);
    const amount = Math.max(0, toWholeNonNegative(qty) || 0);
    if (!safeSku || amount <= 0) return;
    const next = Math.max(0, getPendingQuantity(safeSku) - amount);
    if (next <= 0) pendingQtyBySku.delete(safeSku);
    else pendingQtyBySku.set(safeSku, next);
    notifyGlobal({ type: 'pending-qty', sku: safeSku, pending: getPendingQuantity(safeSku) });
  }

  function isSerialReserved(serial, options) {
    const key = normalizeSerialNumber(serial);
    if (!key) return false;
    if (options?.keepSerials) {
      const keep =
        options.keepSerials instanceof Set
          ? options.keepSerials
          : new Set(
              (Array.isArray(options.keepSerials) ? options.keepSerials : []).map(normalizeSerialNumber)
            );
      if (keep.has(key)) return false;
    }
    return getCartSerialSet(options).has(key) || pendingSerials.has(key);
  }

  function reserveSerials(serials) {
    parseSerialNumbers(serials).forEach((serial) => pendingSerials.add(serial));
    notifyGlobal({ type: 'serials', pending: Array.from(pendingSerials), reserved: Array.from(reservedSerials) });
  }

  function releaseSerials(serials) {
    parseSerialNumbers(serials).forEach((serial) => pendingSerials.delete(serial));
    notifyGlobal({ type: 'serials', pending: Array.from(pendingSerials), reserved: Array.from(reservedSerials) });
  }

  function getReservedSerials(options) {
    return getCartSerialSet(options);
  }

  function filterAvailableOptions(sku, options, filterOptions) {
    const list = Array.isArray(options) ? options : getAvailableOptions(sku);
    const locked = getCartSerialSet(filterOptions);
    /** @type {Set<string>} */
    const keepSerials = new Set();
    if (filterOptions?.keepSerials instanceof Set) {
      filterOptions.keepSerials.forEach((value) => {
        const key = normalizeSerialNumber(value);
        if (key) keepSerials.add(key);
      });
    } else if (Array.isArray(filterOptions?.keepSerials)) {
      filterOptions.keepSerials.forEach((value) => {
        const key = normalizeSerialNumber(value);
        if (key) keepSerials.add(key);
      });
    }
    return list.filter((option) => {
      if (!option || typeof option !== 'object') return false;
      const serial = normalizeSerialNumber(
        /** @type {Record<string, unknown>} */ (option).serialNumber ??
          /** @type {Record<string, unknown>} */ (option).serial
      );
      if (!serial) return false;
      if (keepSerials.has(serial)) return true;
      return !locked.has(serial);
    });
  }

  async function ensureProductsLoaded(productIds) {
    const ids = [...new Set((Array.isArray(productIds) ? productIds : []).map(normalizeId).filter(Boolean))];
    if (!ids.length) return;
    const pending = ids.filter((id) => {
      const entry = productState.get(id);
      return !entry || entry.status === STATUS_IDLE || entry.status === STATUS_LOADING;
    });
    if (!pending.length) return;
    markProductsLoading(pending);
    const api = window.AOneProductWeightsAPI || window.AoneBatchWeight;
    if (!api || typeof api.loadCurrentProductWeights !== 'function') {
      markProductsError(pending, 'WMS_API_UNAVAILABLE');
      return;
    }
    try {
      await api.loadCurrentProductWeights(pending);
      pending.forEach((id) => {
        const entry = productState.get(id);
        if (!entry || entry.status === STATUS_IDLE || entry.status === STATUS_LOADING) {
          markProductError(id, 'WMS_DATA_UNRESOLVED');
        }
      });
    } catch (_error) {
      markProductsError(pending, 'WMS_REQUEST_FAILED');
    }
  }

  function normalizeGuardMessage(type, extra) {
    if (type === 'loading') return LABEL_CHECKING;
    if (type === 'coming-soon') return LABEL_COMING_SOON;
    if (type === 'unavailable') return LABEL_UNAVAILABLE;
    if (type === 'qty') return extra || 'QUANTITY EXCEEDS AVAILABLE STOCK';
    return LABEL_UNAVAILABLE;
  }

  function buildGuardResult(ok, type, message, meta) {
    return {
      ok,
      type,
      message: message || normalizeGuardMessage(type),
      ...meta,
    };
  }

  async function validateSkuRequest(productId, sku, requestedQty, opts) {
    const options = opts || {};
    const safeProductId = normalizeId(productId);
    const safeSku = normalizeSku(sku);
    const qty = Math.max(1, toWholeNonNegative(requestedQty) || 1);

    if (!safeSku) {
      return buildGuardResult(false, 'coming-soon', LABEL_COMING_SOON, { reason: 'SKU_MISSING', maxAllowed: 0 });
    }
    if (!safeProductId) {
      return buildGuardResult(false, 'unavailable', LABEL_UNAVAILABLE, { reason: 'PRODUCT_ID_MISSING' });
    }

    await ensureProductsLoaded([safeProductId]);
    try {
      await fetchCart(Boolean(options.forceCartRefresh));
    } catch (_error) {
      if (options.skipCartCheck !== true) {
        return buildGuardResult(false, 'unavailable', LABEL_UNAVAILABLE, { reason: 'CART_READ_FAILED' });
      }
    }

    const product = getProductAvailability(safeProductId, options);
    if (!product) return buildGuardResult(false, 'unavailable', LABEL_UNAVAILABLE, { reason: 'PRODUCT_UNAVAILABLE' });
    if (product.status === STATUS_LOADING || product.status === STATUS_IDLE) {
      return buildGuardResult(false, 'loading', LABEL_CHECKING, { reason: 'PRODUCT_LOADING' });
    }
    if (product.status !== STATUS_LOADED) {
      return buildGuardResult(false, 'unavailable', LABEL_UNAVAILABLE, { reason: product.errorCode || 'PRODUCT_ERROR' });
    }

    const skuAvailability = getSkuAvailability(safeProductId, safeSku, options);
    if (skuAvailability.status === STATUS_LOADING || skuAvailability.status === STATUS_IDLE) {
      return buildGuardResult(false, 'loading', LABEL_CHECKING, { reason: 'SKU_LOADING' });
    }
    if (skuAvailability.status !== STATUS_LOADED) {
      return buildGuardResult(false, 'unavailable', LABEL_UNAVAILABLE, {
        reason: skuAvailability.reason || 'SKU_UNAVAILABLE',
      });
    }

    const rawCount = Math.max(0, Math.floor(Number(skuAvailability.rawAvailableCount) || 0));
    const effectiveCount = Math.max(0, Math.floor(Number(skuAvailability.count) || 0));
    const storefrontStatus =
      skuAvailability.storefrontStatus || getStorefrontStatus(rawCount, effectiveCount);

    if (!skuAvailability.found || rawCount <= 0) {
      return buildGuardResult(false, 'coming-soon', LABEL_COMING_SOON, {
        reason: skuAvailability.reason || 'SKU_ZERO_OR_NOT_FOUND',
        maxAllowed: 0,
        storefrontStatus: WMS_STATUS_COMING_SOON,
      });
    }

    if (effectiveCount <= 0) {
      return buildGuardResult(false, 'all-in-cart', LABEL_ALL_IN_CART, {
        reason: 'ALL_AVAILABLE_IN_CART',
        maxAllowed: 0,
        storefrontStatus: WMS_STATUS_ALL_IN_CART,
        rawAvailableCount: rawCount,
      });
    }

    const pendingQty = options.includePending === false ? 0 : getPendingQuantity(safeSku);
    // count is already cart-filtered; only subtract in-flight pending reservations.
    const remaining = Math.max(0, effectiveCount - pendingQty);

    if (qty > remaining) {
      return buildGuardResult(false, 'qty', `MAX AVAILABLE QUANTITY: ${remaining}`, {
        reason: 'QTY_EXCEEDS_EFFECTIVE',
        maxAllowed: remaining,
        remaining,
        wmsQuantity: rawCount,
        effectiveQuantity: effectiveCount,
      });
    }

    return buildGuardResult(true, 'ok', LABEL_ADD_TO_CART, {
      maxAllowed: remaining,
      remaining,
      wmsQuantity: rawCount,
      effectiveQuantity: effectiveCount,
      storefrontStatus,
    });
  }

  /**
   * Final client-side serial validation before ATC submit.
   * @param {string[]} serials
   * @param {{ productId?: string, sku?: string, keepSerials?: Set<string>|string[]|null }} [options]
   */
  function validateSelectedSerials(serials, options) {
    const list = parseSerialNumbers(serials);
    if (!list.length) {
      return buildGuardResult(false, 'qty', 'SELECTED WEIGHT IS NO LONGER AVAILABLE', {
        reason: 'SERIALS_MISSING',
      });
    }

    /** @type {Set<string>} */
    const seen = new Set();
    for (let i = 0; i < list.length; i += 1) {
      const serial = list[i];
      if (seen.has(serial)) {
        return buildGuardResult(false, 'qty', 'SELECTED WEIGHT IS NO LONGER AVAILABLE', {
          reason: 'SERIAL_DUPLICATE',
          serial,
        });
      }
      seen.add(serial);
      if (isSerialReserved(serial, options)) {
        return buildGuardResult(false, 'qty', 'SELECTED WEIGHT IS NO LONGER AVAILABLE', {
          reason: 'SERIAL_RESERVED',
          serial,
        });
      }
    }

    const safeSku = normalizeSku(options?.sku);
    const productId = normalizeId(options?.productId) || resolveProductIdForSku(safeSku);
    if (productId && safeSku) {
      const availability = getSkuAvailability(productId, safeSku, options);
      const effectiveSerials = new Set(
        (Array.isArray(availability.availableOptions) ? availability.availableOptions : []).map((option) =>
          normalizeSerialNumber(option?.serialNumber ?? option?.serial)
        )
      );
      for (let i = 0; i < list.length; i += 1) {
        if (!effectiveSerials.has(list[i])) {
          return buildGuardResult(false, 'qty', 'SELECTED WEIGHT IS NO LONGER AVAILABLE', {
            reason: 'SERIAL_NOT_IN_EFFECTIVE',
            serial: list[i],
          });
        }
      }
    }

    return buildGuardResult(true, 'ok', LABEL_ADD_TO_CART, { serials: list });
  }

  async function validateBeforeAdd(payload) {
    const args = payload || {};
    const serials = parseSerialNumbers(args.serialNumbers || args.serials || args.SerialNumbers);
    const serialGuard = validateSelectedSerials(serials, {
      productId: args.productId,
      sku: args.sku,
      keepSerials: args.keepSerials,
      excludeLineKey: args.excludeLineKey,
    });
    if (!serialGuard.ok && serials.length) return serialGuard;

    const guard = await guardVariantAdd({
      productId: args.productId,
      variantId: args.variantId || args.id,
      sku: args.sku,
      quantity: args.quantity,
      forceCartRefresh: args.forceCartRefresh,
      skipCartCheck: args.skipCartCheck,
      keepSerials: args.keepSerials,
      excludeLineKey: args.excludeLineKey,
    });

    if (!guard.ok) return guard;

    if (args.commitPending === true) {
      const sku = normalizeSku(args.sku || getVariantRegistration(args.variantId || args.id)?.sku);
      reservePendingQuantity(sku, args.quantity);
      if (serials.length) reserveSerials(serials);
    }

    return {
      ...guard,
      serials,
      inventory: getInventoryBySku(args.sku),
    };
  }

  async function guardVariantAdd(params) {
    const args = params || {};
    const variantId = normalizeId(args.variantId);
    const qty = Math.max(1, toWholeNonNegative(args.quantity) || 1);
    let productId = normalizeId(args.productId);
    let sku = normalizeSku(args.sku);

    const registration = variantId ? getVariantRegistration(variantId) : null;
    if (!productId && registration) productId = registration.productId;
    if (!sku && registration) sku = registration.sku;

    if (!sku) {
      return buildGuardResult(false, 'coming-soon', LABEL_COMING_SOON, { reason: 'SKU_NOT_RESOLVED', maxAllowed: 0 });
    }
    if (!productId) {
      return buildGuardResult(false, 'unavailable', LABEL_UNAVAILABLE, { reason: 'PRODUCT_NOT_RESOLVED' });
    }
    return validateSkuRequest(productId, sku, qty, args);
  }

  async function guardItems(items, options) {
    const list = Array.isArray(items) ? items : [];
    if (!list.length) return buildGuardResult(false, 'unavailable', LABEL_UNAVAILABLE, { reason: 'NO_ITEMS' });

    try {
      await fetchCart(Boolean(options && options.forceCartRefresh));
    } catch (_error) {
      return buildGuardResult(false, 'unavailable', LABEL_UNAVAILABLE, { reason: 'CART_READ_FAILED' });
    }

    /** @type {Record<string, { requested: number, productId: string, serials: string[] }>} */
    const bySku = Object.create(null);
    for (let i = 0; i < list.length; i += 1) {
      const item = list[i] || {};
      const registration = getVariantRegistration(item.id ?? item.variantId);
      const sku = normalizeSku(item.sku || (item.properties && item.properties.SKU) || registration?.sku);
      const productId = normalizeId(item.productId || registration?.productId);
      const qty = Math.max(0, toWholeNonNegative(item.quantity) || 0);
      const serials = parseSerialNumbers(
        (item.properties && (item.properties.SerialNumbers || item.properties.serialNumbers)) ||
          item.serialNumbers ||
          item.SerialNumbers
      );

      if (serials.length) {
        const serialGuard = validateSelectedSerials(serials, { productId, sku });
        if (!serialGuard.ok) return serialGuard;
      }

      const result = await guardVariantAdd({
        variantId: item.id ?? item.variantId,
        productId,
        sku,
        quantity: qty || 1,
        skipCartCheck: true,
      });
      if (!result.ok) return result;

      if (!sku || !productId || qty <= 0) continue;
      if (!bySku[sku]) bySku[sku] = { requested: 0, productId, serials: [] };
      bySku[sku].requested += qty;
      bySku[sku].serials.push(...serials);
    }

    const skuList = Object.keys(bySku);
    for (let i = 0; i < skuList.length; i += 1) {
      const sku = skuList[i];
      const pack = bySku[sku];
      const availability = getSkuAvailability(pack.productId, sku);
      const rawCount = Math.max(0, Math.floor(Number(availability.rawAvailableCount) || 0));
      const remaining = Math.max(0, Math.floor(Number(availability.count) || 0));

      if (rawCount <= 0) {
        return buildGuardResult(false, 'coming-soon', LABEL_COMING_SOON, {
          reason: 'SKU_ZERO_OR_NOT_FOUND',
          maxAllowed: 0,
        });
      }
      if (remaining <= 0) {
        return buildGuardResult(false, 'all-in-cart', LABEL_ALL_IN_CART, {
          reason: 'ALL_AVAILABLE_IN_CART',
          maxAllowed: 0,
        });
      }
      if (pack.requested > remaining) {
        return buildGuardResult(false, 'qty', `ONLY ${remaining} LEFT FOR THIS SKU`, {
          reason: 'REQUEST_EXCEEDS_EFFECTIVE',
          maxAllowed: remaining,
        });
      }
    }

    return buildGuardResult(true, 'ok', LABEL_ADD_TO_CART);
  }

  function getActionLabelForState(state) {
    if (state === STATUS_LOADING || state === 'loading') return LABEL_CHECKING;
    if (
      state === WMS_STATUS_ALL_IN_CART ||
      state === 'all-in-cart' ||
      state === 'all_in_cart'
    ) {
      return LABEL_ALL_IN_CART;
    }
    // Error / unavailable map to Coming soon — never expose AVAILABILITY UNAVAILABLE.
    if (state === STATUS_ERROR || state === 'error' || state === 'unavailable') return LABEL_COMING_SOON;
    if (state === 'coming-soon' || state === WMS_STATUS_COMING_SOON) return LABEL_COMING_SOON;
    if (state === WMS_STATUS_AVAILABLE || state === 'available') return LABEL_ADD_TO_CART;
    return LABEL_ADD_TO_CART;
  }

  function applyButtonState(button, state, options) {
    if (!(button instanceof HTMLElement)) return;
    const opts = options || {};
    const normalizedState =
      state === WMS_STATUS_AVAILABLE
        ? 'available'
        : state === WMS_STATUS_COMING_SOON
          ? 'coming-soon'
          : state === WMS_STATUS_ALL_IN_CART || state === 'all_in_cart'
            ? 'all-in-cart'
            : state === STATUS_LOADED
              ? 'loaded'
              : state;
    const label = opts.label || getActionLabelForState(normalizedState);
    const disabled =
      opts.disabled !== undefined
        ? Boolean(opts.disabled)
        : normalizedState !== STATUS_LOADED &&
          normalizedState !== 'loaded' &&
          normalizedState !== 'available';
    button.dataset.wmsStatus = String(normalizedState);
    button.setAttribute('aria-busy', normalizedState === STATUS_LOADING || normalizedState === 'loading' ? 'true' : 'false');
    button.setAttribute('aria-disabled', disabled ? 'true' : 'false');
    if (button instanceof HTMLButtonElement) button.disabled = disabled;
    // Keep PLP/Wizzy ATC class contract in sync. CSS uses .plp-card__atc--disabled
    // { pointer-events: none }, so leaving that class after enable blocks Quick View.
    button.classList.toggle('plp-card__atc--disabled', disabled);
    button.classList.toggle('plp-card__atc--active', !disabled);
    const labels = button.querySelectorAll(
      '.add-to-cart-text__content span span, .quick-add__button-text span span, .sticky-add-to-cart__button .add-to-cart-text__content span'
    );
    if (labels.length) {
      labels.forEach((node) => {
        if (node instanceof HTMLElement) node.textContent = label;
      });
    } else {
      const fallback = button.querySelector('.add-to-cart-text__content, .quick-add__button-text');
      if (fallback instanceof HTMLElement) {
        const nested = fallback.querySelector('span span') || fallback.querySelector('span');
        if (nested instanceof HTMLElement) nested.textContent = label;
      } else if (!button.querySelector('[data-add-selected-label]')) {
        // Preserve complex button markup; only rewrite plain text buttons.
        const textHost = button.querySelector('[data-details-weight-label], span');
        if (textHost instanceof HTMLElement && textHost.children.length === 0) {
          textHost.textContent = label;
        }
      }
    }
  }

  /**
   * Update inventory-dependent UI for a card/row/button host.
   * @param {HTMLElement} element
   * @param {ReturnType<typeof normalizeInventoryRecord> & { loading?: boolean, error?: boolean, rawAvailableCount?: number, storefrontStatus?: string }} inventoryData
   */
  function updateComponent(element, inventoryData) {
    if (!(element instanceof HTMLElement) || !inventoryData) return;
    const loading = inventoryData.loading === true;
    const quantity = Math.max(0, Math.floor(Number(inventoryData.quantity) || 0));
    const rawQuantity = Math.max(
      0,
      Math.floor(Number(inventoryData.rawAvailableCount ?? inventoryData.quantity) || 0)
    );
    const storefrontStatus = loading
      ? 'loading'
      : inventoryData.storefrontStatus ||
        getStorefrontStatus(rawQuantity, quantity);
    const status =
      storefrontStatus === WMS_STATUS_AVAILABLE
        ? 'available'
        : storefrontStatus === WMS_STATUS_ALL_IN_CART
          ? 'all-in-cart'
          : storefrontStatus === 'loading'
            ? 'loading'
            : 'coming-soon';
    const available = status === 'available' && quantity > 0;

    element.dataset.wmsStatus = status;
    element.dataset.wmsInventoryStatus =
      status === 'available'
        ? 'available'
        : status === 'all-in-cart'
          ? 'all_in_cart'
          : status === 'coming-soon'
            ? 'coming_soon'
            : status;
    element.dataset.wmsAvailableQty = String(quantity);
    element.dataset.wmsRawAvailableQty = String(rawQuantity);
    element.dataset.variantAvailable = available ? 'true' : 'false';
    if (inventoryData.sku) element.dataset.variantSku = inventoryData.sku;

    const badge = element.querySelector(
      '[data-wms-status-badge], [data-product-sold-out-badge], .premium-pdp-matrix__status, .order-sheet-details-chain-matrix__stock-badge, .order-sheet-details-variant-table__stock-badge'
    );
    if (badge instanceof HTMLElement) {
      if (loading) badge.textContent = LABEL_CHECKING;
      else if (available) badge.textContent = `${LABEL_AVAILABLE} QTY: ${quantity}`;
      else if (status === 'all-in-cart') badge.textContent = `AVAILABLE QTY: 0`;
      else badge.textContent = LABEL_COMING_SOON;
    }

    element.querySelectorAll('[data-product-action], .plp-card__atc, [ref="addToCartButton"], button[name="add"]').forEach(
      (button) => {
        if (!(button instanceof HTMLElement)) return;
        if (loading) {
          applyButtonState(button, 'loading', { disabled: true });
        } else if (status === 'all-in-cart') {
          applyButtonState(button, 'all-in-cart', {
            label: LABEL_ALL_IN_CART,
            disabled: true,
          });
        } else if (!available) {
          applyButtonState(button, 'coming-soon', { disabled: true });
        } else {
          applyButtonState(button, 'available', {
            label: LABEL_ADD_TO_CART,
            disabled: false,
          });
        }
      }
    );

    element.querySelectorAll('[data-details-qty-input], input[name="quantity"]').forEach((input) => {
      if (!(input instanceof HTMLInputElement)) return;
      const remaining = Math.max(0, getRemainingQuantity(inventoryData.sku));
      const max = available ? remaining || quantity : 0;
      input.max = String(max);
      input.dataset.variantInventoryQty = String(max);
      if (!available || max <= 0) {
        input.disabled = true;
        input.setAttribute('aria-disabled', 'true');
      } else if (input.dataset.wmsForceDisabled !== 'true') {
        input.disabled = false;
        input.setAttribute('aria-disabled', 'false');
        const current = toWholeNonNegative(input.value) || 1;
        if (current > max) input.value = String(max);
        if (current < 1) input.value = '1';
      }
    });
  }

  function subscribe(callback) {
    if (typeof callback !== 'function') return function noop() {};
    globalSubscribers.add(callback);
    return function unsubscribe() {
      globalSubscribers.delete(callback);
    };
  }

  function subscribeToCartChanges(callback) {
    if (typeof callback !== 'function') return function noop() {};
    cartChangeSubscribers.add(callback);
    return function unsubscribe() {
      cartChangeSubscribers.delete(callback);
    };
  }

  function notifyCartInventoryUpdated(cart, reason) {
    const serialNumbers = Array.from(getCartSerialSet({ cart, includePending: false }));
    /** @type {string[]} */
    const affectedProductIds = [];
    /** @type {string[]} */
    const affectedVariantIds = [];
    /** @type {string[]} */
    const affectedSkus = [];
    if (cart && Array.isArray(cart.items)) {
      cart.items.forEach((item) => {
        if (!item || typeof item !== 'object') return;
        const productId = normalizeId(item.product_id || item.productId);
        const variantId = normalizeId(item.variant_id || item.id);
        const sku = normalizeSku(item.sku || (item.properties && item.properties.SKU));
        if (productId && !affectedProductIds.includes(productId)) affectedProductIds.push(productId);
        if (variantId && !affectedVariantIds.includes(variantId)) affectedVariantIds.push(variantId);
        if (sku && !affectedSkus.includes(sku)) affectedSkus.push(sku);
      });
    }
    const detail = {
      cart,
      serialNumbers,
      cartSerialNumbers: serialNumbers,
      affectedProductIds,
      affectedVariantIds,
      affectedSkus,
      generation: cartState.appliedGeneration || cartState.generation,
      mutationGeneration: cartState.mutationGeneration,
      reason: reason || 'cart-update',
      updatedAt: Date.now(),
    };
    cartChangeSubscribers.forEach((callback) => {
      try {
        callback(detail);
      } catch (_error) {
        /* avoid breaking other listeners */
      }
    });
    notifyGlobal({ type: 'cart', ...detail });
    productState.forEach((_entry, productId) => {
      notifyProduct(productId);
    });
    try {
      document.dispatchEvent(new CustomEvent(CART_INVENTORY_EVENT, { detail }));
    } catch (_error) {
      /* CustomEvent unavailable */
    }
    try {
      document.dispatchEvent(new CustomEvent(INVENTORY_STATE_EVENT, { detail }));
    } catch (_error) {
      /* CustomEvent unavailable */
    }
  }

  async function refreshInventory(productIdsOrSkus) {
    const values = Array.isArray(productIdsOrSkus) ? productIdsOrSkus : [productIdsOrSkus];
    const productIds = [];
    values.forEach((value) => {
      const asId = normalizeId(value);
      const asSku = normalizeSku(value);
      if (asId && /^\d+$/.test(asId)) productIds.push(asId);
      else if (asSku) {
        const productId = resolveProductIdForSku(asSku);
        if (productId) productIds.push(productId);
      }
    });
    await ensureProductsLoaded(productIds);
    notifyGlobal({ type: 'refresh', productIds });
    return productIds.map((id) => getProductAvailability(id));
  }

  function syncFormDataset(form, payload) {
    if (!(form instanceof HTMLElement) || !payload) return;
    const variantId = normalizeId(payload.variantId);
    const sku = normalizeSku(payload.sku);
    const productId = normalizeId(payload.productId || form.dataset.productId || getFormProductId(form));
    if (variantId) form.dataset.wmsVariantId = variantId;
    if (sku) form.dataset.wmsVariantSku = sku;
    if (productId) form.dataset.productId = productId;
    if (variantId && productId && sku) registerVariant(variantId, productId, sku);
  }

  function getFormVariantContext(form) {
    const variantId = normalizeId(form?.dataset?.wmsVariantId || getFormVariantId(form));
    const productId = normalizeId(form?.dataset?.productId || getFormProductId(form));
    const sku = normalizeSku(form?.dataset?.wmsVariantSku);
    return { productId, variantId, sku };
  }

  async function ensureCartSerialsSynced(forceRefresh) {
    const cart = await fetchCart(Boolean(forceRefresh));
    syncReservedSerialsFromCart(cart);
    return cart;
  }

  function clearCartCache() {
    cartState.data = null;
    cartState.ts = 0;
    cartState.ready = false;
    cartState.error = null;
  }

  function isCartStateReady() {
    return cartState.ready === true && cartState.data != null;
  }

  function getCartStateMeta() {
    return {
      ready: isCartStateReady(),
      generation: cartState.generation,
      appliedGeneration: cartState.appliedGeneration,
      mutationGeneration: cartState.mutationGeneration,
      error: cartState.error,
      pending: Boolean(cartState.pending),
    };
  }

  async function onCartUpdated(event) {
    const cartFromEvent = event?.detail?.resource || event?.detail?.cart || null;
    const reason =
      event?.detail?.data?.source ||
      event?.detail?.sourceId ||
      (event?.type === 'cart:updated' ? 'cart-updated' : 'cart-update');
    const requestGeneration = ++cartState.generation;
    try {
      let cart;
      if (cartFromEvent && Array.isArray(cartFromEvent.items)) {
        cart = cartFromEvent;
        if (requestGeneration === cartState.generation) {
          cartState.data = cart;
          cartState.ts = Date.now();
          cartState.ready = true;
          cartState.error = null;
          cartState.appliedGeneration = requestGeneration;
        }
      } else {
        clearCartCache();
        cart = await fetchCart(true);
      }
      if (requestGeneration !== cartState.generation && cartState.data) {
        cart = cartState.data;
      }
      if (!cart) return;
      syncReservedSerialsFromCart(cart);
      pendingSerials.clear();
      pendingQtyBySku.clear();
      incrementCartGeneration();
      notifyCartInventoryUpdated(cart, reason);
    } catch (_error) {
      warnDev('Unable to reconcile WMS reservations after cart update.');
    }
  }

  async function onPageShow(event) {
    const navEntries =
      typeof performance !== 'undefined' && typeof performance.getEntriesByType === 'function'
        ? performance.getEntriesByType('navigation')
        : [];
    const navType = navEntries && navEntries[0] ? navEntries[0].type : '';
    const restoredFromCache = Boolean(event?.persisted) || navType === 'back_forward';
    if (!restoredFromCache) return;

    // Synchronously skeletonize before awaiting cart — never flash Select Weight / stale qty.
    markCartDependentInventoryLoading('pageshow');
    try {
      const cart = await fetchCart(true);
      notifyCartInventoryUpdated(cart, 'pageshow');
    } catch (_error) {
      warnDev('Unable to refresh cart after pageshow.');
    }
  }

  // Warm shared cart serial state early so first paint can be cart-aware.
  fetchCart(false)
    .then((cart) => {
      syncReservedSerialsFromCart(cart);
      notifyCartInventoryUpdated(cart, 'initial-load');
    })
    .catch(() => {
      /* non-blocking */
    });

  document.addEventListener('cart:update', onCartUpdated);
  document.addEventListener('cart:updated', onCartUpdated);
  window.addEventListener('pageshow', onPageShow);

  const publicApi = {
    STATUS_IDLE,
    STATUS_LOADING,
    STATUS_LOADED,
    STATUS_ERROR,
    WMS_STATUS_AVAILABLE,
    WMS_STATUS_COMING_SOON,
    WMS_STATUS_ALL_IN_CART,
    LABEL_ADD_TO_CART,
    LABEL_CHECKING,
    LABEL_COMING_SOON,
    LABEL_AVAILABLE,
    LABEL_ALL_IN_CART,
    LABEL_UNAVAILABLE,
    CART_INVENTORY_EVENT,
    INVENTORY_STATE_EVENT,
    getWmsStatus,
    getStorefrontStatus,
    resolveVariantAvailabilityState,
    getInventorySkeletonMarkup,
    formatMinimumWeightLabel,
    resolveWeightActionLabel,
    resolveWeightButtonState,
    calculateEffectiveAvailability,
    normalizeSku,
    normalizeId,
    normalizeSerialNumber,
    normalizeSerial,
    parseSerialNumbers,
    parseSerialList,
    buildWeightGroupsFromOptions,
    getEffectiveSkuInventory,
    getEffectiveProductInventory,
    normalizeWmsProductBatchResponse,
    normalizeInventoryRecord,
    emptyInventoryRecord,
    getInventoryBySku,
    getAvailableQuantity,
    getStatus,
    isAvailable,
    getAvailableOptions,
    getAvailableWeights,
    getMinimumWeight,
    getCartReservedQuantity,
    getPendingQuantity,
    getRemainingQuantity,
    reservePendingQuantity,
    releasePendingQuantity,
    isSerialReserved,
    reserveSerials,
    releaseSerials,
    getReservedSerials,
    getCartSerialSet,
    getEditWeightSerialContext,
    filterAvailableOptions,
    validateSelectedSerials,
    validateBeforeAdd,
    updateComponent,
    subscribe,
    subscribeToCartChanges,
    refreshInventory,
    getProductAvailability,
    getSkuAvailability,
    isProductAvailable,
    isSkuAvailable,
    getSkuAvailableQuantity,
    subscribeToProductAvailability,
    markProductsLoading,
    markProductsError,
    upsertBatchProducts,
    ensureProductsLoaded,
    registerVariant,
    getVariantRegistration,
    guardVariantAdd,
    guardItems,
    applyButtonState,
    syncFormDataset,
    getFormVariantContext,
    getFormRequestedQty,
    fetchCart,
    getCurrentCart,
    refreshCartState,
    ensureCartSerialsSynced,
    isCartStateReady,
    getCartStateMeta,
    applyCart,
    invalidateCart,
    markCartDependentInventoryLoading,
    incrementCartGeneration,
    handleSuccessfulCartMutation,
    warnDev,
  };

  window.AOneWmsAvailability = publicApi;
  window.WMSInventory = publicApi;
})();
